import React, {useContext, useState} from 'react';

import { useTranslation } from "react-i18next";

import { AuthContext } from '../../context/authContext';

import {boardUrl, logoUrl, NavigationItems} from '../../config/config';

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
	faBars, faBell,
	faCogs, faComments,
	faInfoCircle, faMoneyBill,
	faSearch,
	faSignIn,
	faSignOut, faUnlock,
	faUser
} from "@fortawesome/free-solid-svg-icons";

import {MenuItem, MenuDivider} from '@szhsin/react-menu';
import '@szhsin/react-menu/dist/index.css';
import '@szhsin/react-menu/dist/transitions/slide.css';
import PopupMenu from "./PopupMenu";
import CanvasMenu from "./CanvasMenu";

const AccountMenu = ({user}) => {
	const { i18n, t } = useTranslation();

	return (user.user_id) ? (
		<div className="szh-menu-container-background">
			<MenuItem href={user.view_url}>
				<FontAwesomeIcon icon={faUser} className="mr-2"/> {t('profile_page')}
			</MenuItem>

			<MenuItem href={`${boardUrl}/index.php?dbtech-credits`}>
				<FontAwesomeIcon icon={faMoneyBill} className="mr-2"/>
				{t('credits')}: <b className="inline-block text-red-500 ml-2">{user.credits_formatted_short}$</b>
			</MenuItem>

			<MenuItem href={`${boardUrl}/index.php?direct-messages`}>
				<FontAwesomeIcon icon={faComments} className="mr-2"/>
				{t('messages')}:
				<b className="inline-flex justify-center items-center w-[24px] h-[24px] rounded-full bg-red-500 text-white text-xs ml-2">
					{user.conversations_unread}
				</b>
			</MenuItem>

			<MenuItem href={`${boardUrl}/index.php?account/alerts`}>
				<FontAwesomeIcon icon={faBell} className="mr-2"/>
				{t('alerts')}:
				<b className="inline-flex justify-center items-center w-[24px] h-[24px] rounded-full bg-red-500 text-white text-xs ml-2">
					{user.alerts_unviewed}
				</b>
			</MenuItem>

			<MenuItem href={`${boardUrl}/index.php?account`}>
				<FontAwesomeIcon icon={faInfoCircle} className="mr-2"/> {t('account_info')}
			</MenuItem>

			<MenuItem href={`${boardUrl}/index.php?account/security`}>
				<FontAwesomeIcon icon={faCogs} className="mr-2"/> {t('settings')}
			</MenuItem>

			<MenuDivider/>

			<MenuItem href={`${boardUrl}/logout`}>
				<FontAwesomeIcon icon={faSignOut} className="mr-2"/> {t('logout')}
			</MenuItem>
		</div>
	) : (
		<div className="szh-menu-container-background">
			<MenuItem href={`${boardUrl}/index.php?login&universal=1`}>
				<FontAwesomeIcon icon={faSignIn} className="mr-2"/> {t('login')}
			</MenuItem>

			<MenuItem href={`${boardUrl}/index.php?register&universal=1`}>
				<FontAwesomeIcon icon={faUnlock} className="mr-2"/> {t('register')}
			</MenuItem>
		</div>
	)
}

const Header = () => {
	const {visitor} = useContext(AuthContext);
	const {i18n, t} = useTranslation();

	const [isMenuOpen, setIsMenuOpen] = useState(false);

	let unread = 0;
	if (visitor && visitor.conversations_unread) {
		unread += Number.parseInt(visitor.conversations_unread);
	}
	if (visitor && visitor.alerts_unviewed) {
		unread += Number.parseInt(visitor.alerts_unviewed);
	}

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	if (visitor) {
		return (
			<header className="shadow-lg tracking-wide relative z-50">
				<div className="hidden header-inner relative w-auto mx-4 items-center h-[139px] px-4 mt-2 lg:flex">
					<div className="header-inner-left absolute top-0 left-0 h-[139px] w-[470px] z-30"></div>

					<a href={boardUrl} className="ml-auto mr-auto relative z-40 lg:mr-0">
						<img src={logoUrl} alt="KNV" className="w-[100px] h-[36px]"/>
					</a>

					<div className="header-inner-right absolute top-0 right-0 h-[139px] w-[470px] z-30"></div>
				</div>

				<div className="header-nav w-auto md:mx-4">
					<div className="py-2 px-4 flex items-center justify-between gap-4 w-full">
						<div className="hidden ml-0 items-center gap-x-6 lg:flex">
							{NavigationItems.map((item, i) => (
								<a key={i} href={item.url} className="text-white font-semibold hover:underline">
									{item.title}
								</a>
							))}
						</div>

						<div className="inline-flex items-center lg:hidden">
							<span className="font-semibold text-white" onClick={toggleMenu}>
								<FontAwesomeIcon icon={faBars} className="mr-2"/>
							</span>

							<a href={boardUrl} className="">
								<img src={logoUrl} alt="KNV" className="w-auto h-[24px] ml-2"/>
							</a>
						</div>

						<div className='flex items-center ml-auto space-x-6 md:mr-0'>
							<div className="h-[32px] inline-flex items-center">
								<PopupMenu label={
									<div className="relative inline-flex items-center">
										<span className="flex w-[32px] h-[32px] items-center justify-center rounded-full bg-primary text-white hover:bg-secondary">
											{(visitor && visitor.user_id && visitor.avatar_urls.s) ? (
												<img src={visitor.avatar_urls.s} alt={visitor.username} className="w-full h-full object-cover rounded-full" />
											) : (
												<FontAwesomeIcon icon={faUser} />
											)}
										</span>

										<button
											className="hidden font-semibold text-[15px] border-none outline-none text-white ml-2 hover:underline lg:block">
											{(visitor && visitor.user_id) ? visitor.username : t('login')}
										</button>

										{unread > 0 && (
											<b className="absolute -top-2 -right-2 inline-flex justify-center items-center w-[18px] h-[18px] rounded-full bg-red-500 text-white text-xs ml-2">
												{unread}
											</b>
										)}
									</div>
								}>
									<AccountMenu user={visitor}/>
								</PopupMenu>
							</div>

							<a href={`${boardUrl}/search`} className="flex w-[32px] h-[32px] items-center justify-center mr-2 rounded-full bg-primary text-white hover:bg-secondary">
								<FontAwesomeIcon icon={faSearch}/>
							</a>
						</div>
					</div>
				</div>

				<CanvasMenu isOpen={isMenuOpen} toggleMenu={toggleMenu} />
			</header>
		);
	}
};

export default Header;